import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { SharedCommonUtility } from '@shared/utils/common.utility';
import { IHttpQueryOptions } from '../../../shared/interfaces/http.query.interface';
import { IAsyncReportResponse, IAutoVpatReportDisclaimerQuery } from '../../../shared/interfaces/async-report.interface';
import { AutoVpatType } from '../../../shared/constants/async-report.constants';
import { AdminRestAPI } from './rest/admin.api';
import {
  IManualAudit,
  IManualEvalCreateRequest,
  IManualAuditServerResponse,
  IPopulatedManualAudit,
  IManualAuditUpdateRequest,
  IManualEvalCopyRequest,
} from '../../../shared/interfaces/manual-audit.interface';
import { RestBuilder } from './helpers/rest.builder';
import { RestService } from './rest.service';
import { ITestGuideSimpleServerResponse } from '../../../shared/interfaces/test-guide.interface';
import { IManualScanEdit, IScan } from '../../../shared/interfaces/scan.interface';
import { IScopingCrawl, IStartScopingCrawlBody } from '../../../shared/interfaces/scoping-crawl.interface';
import { TestGuidesApi } from './rest/test-guides.api';
import { WorkspacesRestAPI } from './rest/workspaces.api';
import { EvaluationStandards } from '../../../shared/constants/manual-evaluation';
import { standardNameAndLevel } from '../../../shared/constants/scanning';
import { UserPropertyService } from './user-property.service';
import { ICurrentSelectedProperty } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class ManualAuditService {
  private restBuilder: RestBuilder;

  constructor(
    private adminRestAPI: AdminRestAPI,
    private restService: RestService,
    private testGuidesApi: TestGuidesApi,
    private workspacesApi: WorkspacesRestAPI,
    private userPropertyService: UserPropertyService,
  ) {
    this.restBuilder = new RestBuilder();
  }

  public createManualAuditWithFile(workspaceId: string, digitalPropertyId: string, formData: FormData): Observable<IManualAudit> {
    return this.adminRestAPI.createManualAuditWithFile(workspaceId, digitalPropertyId, formData);
  }

  public createEmptyEvaluation(
    workspaceId: string,
    digitalPropertyId: string,
    request: IManualEvalCreateRequest,
    options: { useAdminApi: boolean } = { useAdminApi: true },
  ): Observable<IManualAudit> {
    if (options.useAdminApi) {
      return this.adminRestAPI.createManualAudit(workspaceId, digitalPropertyId, request);
    }
    return this.workspacesApi.createEmptyEvaluation(workspaceId, digitalPropertyId, request);
  }

  public createScopingEvaluation(
    workspaceId: string,
    digitalPropertyId: string,
    request: IManualEvalCreateRequest,
    options: { useAdminApi: boolean } = { useAdminApi: true },
  ): Observable<IManualAudit> {
    if (options.useAdminApi) {
      return this.adminRestAPI.createScopingManualAudit(workspaceId, digitalPropertyId, request);
    }
    return this.workspacesApi.createScopingEvaluation(workspaceId, digitalPropertyId, request);
  }

  public startScopingCrawl(
    workspaceId: string,
    digitalPropertyId: string,
    manualEvaluationId: string,
    body: IStartScopingCrawlBody,
  ): Observable<IScopingCrawl> {
    return this.workspacesApi.startScopingCrawl(workspaceId, digitalPropertyId, manualEvaluationId, body);
  }

  public getEvaluation(
    workspaceId: string,
    digitalPropertyId: string,
    manualEvaluationId: string,
    options: { useAdminApi: boolean } = { useAdminApi: true },
  ): Observable<IManualScanEdit> {
    return options.useAdminApi
      ? this.adminRestAPI.getManualAuditWithPages(workspaceId, digitalPropertyId, manualEvaluationId)
      : this.workspacesApi.getEvaluationMetadata(workspaceId, digitalPropertyId, manualEvaluationId);
  }

  public patchEvaluation(
    workspaceId: string,
    digitalPropertyId: string,
    manualEvaluationId: string,
    request: IManualEvalCreateRequest,
    options: { useAdminApi: boolean } = { useAdminApi: true },
  ): Observable<IManualAudit> {
    return options.useAdminApi
      ? this.adminRestAPI.patchManualAudit(workspaceId, digitalPropertyId, manualEvaluationId, request)
      : this.workspacesApi.patchEvaluationMetadata(workspaceId, digitalPropertyId, manualEvaluationId, request);
  }

  public archiveEvaluation(
    workspaceId: string,
    digitalPropertyId: string,
    manualEvaluationId: string,
    options: { useAdminApi: boolean } = { useAdminApi: true },
  ): Observable<IPopulatedManualAudit> {
    return options.useAdminApi
      ? this.adminRestAPI.archiveManualAudit(workspaceId, digitalPropertyId, manualEvaluationId)
      : this.workspacesApi.archiveEvaluationMetadata(workspaceId, digitalPropertyId, manualEvaluationId);
  }

  public getAllManualAudits(
    queryOptions: IHttpQueryOptions,
    options?: { skipLoader?: true },
  ): Observable<IManualAuditServerResponse> {
    return this.adminRestAPI.getAllManualAudits(queryOptions, options);
  }

  public getManualAuditDownload(resourceId: string): string {
    return this.restBuilder.create().secureResources(resourceId).getApiUrl();
  }

  public manualAuditDownloadFile(resourceId: string): Observable<void> {
    const url: string = this.getManualAuditDownload(resourceId);
    return this.restService.download(url);
  }

  public manualUploadFile(
    workspaceId: string,
    digitalPropertyId: string,
    existingManualAuditId: string,
    formData: FormData,
  ): Observable<IManualAudit> {
    return this.adminRestAPI.manualUploadFile(workspaceId, digitalPropertyId, existingManualAuditId, formData);
  }

  public getManualAuditsForDigitalProperty(workspaceId: string, digitalPropertyId: string): Observable<IPopulatedManualAudit[]> {
    return this.adminRestAPI.getAllManualAuditsForDigitalProperty(workspaceId, digitalPropertyId);
  }

  public updateManualAudit(
    workspaceId: string,
    digitalPropertyId: string,
    scanId: string,
    updateRequest: IManualAuditUpdateRequest,
  ): Observable<IPopulatedManualAudit> {
    return this.adminRestAPI.updateManualAudit(workspaceId, digitalPropertyId, scanId, updateRequest);
  }

  public setGoldenEvaluation(
    workspaceId: string,
    digitalPropertyId: string,
    scanId: string,
    isGoldenEvaluation: boolean,
  ): Observable<IPopulatedManualAudit> {
    return this.adminRestAPI.setGoldenEvaluation(workspaceId, digitalPropertyId, scanId, { isGoldenEvaluation });
  }

  public exportGoldenDataset(): Observable<void> {
    const url: string = this.restBuilder.create().admin().manualAudit().golden().export().getApiUrl();
    return this.restService.download(url);
  }

  public runAiTesting(workspaceId: string, digitalPropertyId: string, scanId: string): Observable<void> {
    return this.adminRestAPI.runAiTesting(workspaceId, digitalPropertyId, scanId);
  }

  public copyManualAudit(
    workspaceId: string,
    digitalPropertyId: string,
    manualAuditId: string,
    body: IManualEvalCopyRequest,
  ): Observable<void> {
    return this.workspacesApi.copyManualAudit(workspaceId, digitalPropertyId, manualAuditId, body);
  }

  public getTestGuideNames(dpId?: string, filterLegacyEA: boolean = false): Observable<ITestGuideSimpleServerResponse> {
    return this.testGuidesApi.getTestGuideNames(dpId, filterLegacyEA);
  }

  public updateEvaluationStandards(
    workspaceId: string,
    digitalPropertyId: string,
    scanId: string,
    standards: EvaluationStandards[],
    conformanceLevel: standardNameAndLevel,
  ): Observable<IScan> {
    return this.workspacesApi.updateManualEvaluationStandardsById(
      workspaceId,
      digitalPropertyId,
      scanId,
      standards,
      conformanceLevel,
    );
  }

  public getAutoVpatReport(evaluationIdsKey: string, vpatType: AutoVpatType): Observable<IAsyncReportResponse> {
    return this.userPropertyService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ workspaceId, digitalPropertyId }: ICurrentSelectedProperty) =>
          this.restService.getAutoVpatReport(workspaceId, digitalPropertyId, evaluationIdsKey, vpatType),
        ),
      );
  }

  public generateAutoVpatReport(
    evaluationIdsKey: string,
    vpatType: AutoVpatType,
    disclaimer?: { disclaimerVersion: string; disclaimerTranslationKey: string; consentedAt: Date },
  ): Observable<IAsyncReportResponse> {
    let disclaimerData: IAutoVpatReportDisclaimerQuery;
    if (SharedCommonUtility.notNullish(disclaimer)) {
      disclaimerData = {
        ...disclaimer,
        consentedAt: disclaimer.consentedAt.toISOString(),
      };
    }

    return this.userPropertyService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ workspaceId, digitalPropertyId }: ICurrentSelectedProperty) =>
          this.restService.generateAutoVpatReport(workspaceId, digitalPropertyId, evaluationIdsKey, vpatType, disclaimerData),
        ),
      );
  }
}
